<template>
  <section class="card report-kpis" aria-labelledby="summaryTitle">
    <h2 class="card-title" id="summaryTitle"><span class="ttl">Summary</span></h2>
    <div class="kpi-strip">
      <div v-for="cell in cells" :key="cell.label" class="kpi-cell">
        <div class="kpi-label">{{ cell.label }}</div>
        <div v-if="cell.sub" class="kpi-sub">{{ cell.sub }}</div>
        <div class="kpi-value" :class="cell.tone ? `is-${cell.tone}` : ''">{{ cell.value }}</div>
      </div>
    </div>
  </section>
</template>

<script>
  import { formatPercent } from '../../utils/reportFormat';
  import { AREA_DECIMALS } from './reportConstants';

  export default {
    name: 'ReportKpis',
    props: {
      /** computeKpis() output: { total, corrosion, coatingBreakdown, scabCandidates, averageConfidence, areaMetric } */
      kpis: { type: Object, required: true },
    },
    computed: {
      cells() {
        const k = this.kpis;
        return [
          { label: 'Total findings', value: k.total },
          { label: 'Corrosion findings', value: k.corrosion, tone: 'alert' },
          { label: 'Coating breakdown', value: k.coatingBreakdown },
          { label: 'Potential scab candidates', value: k.scabCandidates, tone: 'alert' },
          { label: 'Average confidence', value: formatPercent(k.averageConfidence), tone: 'good' },
          { label: 'Area metric', sub: '(relative visible area %)', value: formatPercent(k.areaMetric, AREA_DECIMALS) },
        ];
      },
    },
  };
</script>

<style lang="scss" scoped>
  /* Summary strip: six cells divided by rules, label above value (PM mockup) */
  .kpi-strip {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    border-top: 1px solid var(--aa-border-default);
  }
  .kpi-cell {
    padding: 10px 12px 12px;
    border-right: 1px solid var(--aa-border-default);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 6px;
    min-width: 0;

    &:last-child { border-right: none; }
  }
  .kpi-label {
    font-size: var(--aa-font-micro);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--aa-text-secondary);
    text-align: center;
    line-height: 1.3;
  }
  // Caption size, not micro: micro is for the uppercase labels and chips, and 11px lower-case prose reads as tiny.
  .kpi-sub { display: block; font-size: var(--ds-font-caption); color: var(--aa-text-muted); text-align: center; line-height: 1.4; margin-top: -2px; }
  .kpi-value {
    font-family: var(--ds-font-family-heading) !important; // beats the app's global body-font rule
    font-size: 26px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: var(--aa-text-primary);

    &.is-alert { color: var(--aa-kpi-alert); }
    &.is-good { color: var(--aa-kpi-good); }
  }

  @media (max-width: 1100px) {
    .kpi-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .kpi-cell:nth-child(3) { border-right: none; }
    .kpi-cell:nth-child(n + 4) { border-top: 1px solid var(--aa-border-default); }
  }
  @media (max-width: 760px) {
    .kpi-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .kpi-cell:nth-child(3) { border-right: 1px solid var(--aa-border-default); }
    .kpi-cell:nth-child(even) { border-right: none; }
    .kpi-cell:nth-child(n + 3) { border-top: 1px solid var(--aa-border-default); }
  }
</style>
